import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import '../styles/components/header.scss';
import { AuthContext } from '../context/AuthContext';
import { baseURL, CURRENT_SEASON } from '../Constants';
import SearchBar from './SearchBar';

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [infoDropdownOpen, setInfoDropdownOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const { user, setToken, isAdmin, adminLogin, token } = useContext(AuthContext);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const infoDropdownRef = useRef<HTMLLIElement>(null);
  
  // Helper function to determine if matches button should be active
  const isMatchesActive = () => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    return location.pathname === '/matches' && (!tab || tab === 'matches');
  };
  
  // Helper function to determine if bracket button should be active
  const isBracketActive = () => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    return location.pathname === '/matches' && tab === 'bracket';
  };
  
  // Helper function to determine if info button should be active
  const isInfoActive = () => {
    return location.pathname.startsWith('/info');
  };
  
  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setInfoDropdownOpen(false);
    setMobileInfoOpen(false);
  }, [location.pathname]);
  
  // Prevent scrolling when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (infoDropdownRef.current && !infoDropdownRef.current.contains(event.target as Node)) {
        setInfoDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef, infoDropdownRef]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  
  const toggleInfoDropdown = () => {
    setInfoDropdownOpen(!infoDropdownOpen);
  };
  
  const closeMenu = () => {
    setMenuOpen(false);
    setInfoDropdownOpen(false);
  };

  const handleSteamLogin = () => {
    closeMenu();
    window.location.href = `${baseURL}/Auth/steam`;
  };

  const handleAdminLogin = () => {
    setDropdownOpen(false);
    // This will navigate to the admin login page
    // The actual admin authentication will happen there using the adminLogin function
  };

  const handleLogout = () => {
    setToken(null); // This will clear both regular and admin tokens
    setDropdownOpen(false);
    closeMenu();
  };

  // const getProfileImage = () => {
  //   return user?.avatarUrl || 'https://via.placeholder.com/40';
  // };

  return (
    <header className="site-header">
      <div className="container">
        <div className="logo-container">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img src="/images/ncslogo.png" alt="Northern Championship Series" className="site-logo" />
          </Link>
        </div>
        
        {/* Hamburger Button */}
        <button
          className={`hamburger ${menuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span className="hamburger-box">
            <span className="hamburger-inner"></span>
          </span>
        </button>
        
        {/* Mobile Navigation */}
        <nav className={`mobile-nav ${menuOpen ? 'active' : ''}`}>
          {/* Mobile menu header */}
          <div className="mobile-nav-header">
            <Link to="/" className="logo" onClick={closeMenu}>
              <img src="/images/ncslogo.png" alt="NCS" className="site-logo" />
            </Link>
            <button className="close-button" onClick={closeMenu} aria-label="Close menu">
              <span>&times;</span>
            </button>
          </div>

          {/* Search bar inside menu */}
          <div className="mobile-nav-search">
            <SearchBar isMobile={true} onClose={closeMenu} />
          </div>

          <ul>
            <li>
              <NavLink 
                to="/" 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={closeMenu}
              >
                Home
              </NavLink>
            </li>
            <li>
              <NavLink 
                to="/news" 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={closeMenu}
              >
                News
              </NavLink>
            </li>
            <li>
              <NavLink 
                to="/matches?tab=matches" 
                className={() => isMatchesActive() ? 'active' : ''}
                onClick={closeMenu}
              >
                Matches
              </NavLink>
            </li>
            <li>
              <NavLink 
                to={`/teams?season=${CURRENT_SEASON}`} 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={closeMenu}
              >
                Teams
              </NavLink>
            </li>
            <li>
              <NavLink 
                to="/leaderboard" 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={closeMenu}
              >
                Leaderboard
              </NavLink>
            </li>
            <li>
              <NavLink 
                to={`/matches?tab=bracket&season=${CURRENT_SEASON}`} 
                className={() => isBracketActive() ? 'active' : ''}
                onClick={closeMenu}
              >
                Bracket
              </NavLink>
            </li>
            <li className="info-menu-item">
              <div className={`info-dropdown ${isInfoActive() ? 'active' : ''}`}>
                <button
                  className={`info-label ${mobileInfoOpen ? 'open' : ''}`}
                  onClick={() => setMobileInfoOpen(!mobileInfoOpen)}
                  aria-expanded={mobileInfoOpen}
                >
                  Info
                  <span className="dropdown-arrow">▼</span>
                </button>
                <div className={`info-submenu ${mobileInfoOpen ? 'open' : ''}`}>
                  <NavLink
                    to="/info/rules"
                    className={({ isActive }) => isActive ? 'active' : ''}
                    onClick={closeMenu}
                  >
                    Rules
                  </NavLink>
                  <NavLink
                    to="/info/prizepool"
                    className={({ isActive }) => isActive ? 'active' : ''}
                    onClick={closeMenu}
                  >
                    Prize Pool
                  </NavLink>
                  <NavLink
                    to="/info/format"
                    className={({ isActive }) => isActive ? 'active' : ''}
                    onClick={closeMenu}
                  >
                    Format
                  </NavLink>
                  <NavLink
                    to="/info/schedule"
                    className={({ isActive }) => isActive ? 'active' : ''}
                    onClick={closeMenu}
                  >
                    Schedule
                  </NavLink>
                  <NavLink
                    to="/info/faq"
                    className={({ isActive }) => isActive ? 'active' : ''}
                    onClick={closeMenu}
                  >
                    FAQ
                  </NavLink>
                  <NavLink
                    to="/about"
                    className={({ isActive }) => isActive ? 'active' : ''}
                    onClick={closeMenu}
                  >
                    About Us
                  </NavLink>
                </div>
              </div>
            </li>
            {/* Show Admin link for admin users */}
            {isAdmin && (
              <li>
                <NavLink
                  to="/admin/dashboard"
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={closeMenu}
                >
                  Admin Panel
                </NavLink>
              </li>
            )}
            {user ? (
              <li className="login-mobile">
                <Link 
                  to={`/profile/${user.steamID}`} 
                  className="profile-button-mobile"
                  onClick={closeMenu}
                >
                  {/* <img src={getProfileImage()} alt="Profile" className="profile-pic" /> */}
                  <span>{user.playerName}</span>
                </Link>
              </li>
            ) : (
              <li className="login-mobile">
                <button className="login-button" onClick={handleSteamLogin}>
                  Login with Steam
                </button>
              </li>
            )}
          </ul>
        </nav>
        
        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <ul>
            <li>
              <NavLink 
                to="/" 
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                Home
              </NavLink>
            </li>
            <li>
              <NavLink 
                to="/news" 
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                News
              </NavLink>
            </li>
            <li>
              <NavLink 
                to="/matches?tab=matches" 
                className={() => isMatchesActive() ? 'active' : ''}
              >
                Matches
              </NavLink>
            </li>
            <li>
              <NavLink 
                to={`/teams?season=${CURRENT_SEASON}`} 
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                Teams
              </NavLink>
            </li>
            <li>
              <NavLink 
                to="/leaderboard" 
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                Leaderboard
              </NavLink>
            </li>
            <li>
              <NavLink 
                to={`/matches?tab=bracket&season=${CURRENT_SEASON}`} 
                className={() => isBracketActive() ? 'active' : ''}
              >
                Bracket
              </NavLink>
            </li>
            <li className="info-menu-item" ref={infoDropdownRef}>
              <button 
                className={`info-dropdown-button ${isInfoActive() ? 'active' : ''}`}
                onClick={toggleInfoDropdown}
                aria-expanded={infoDropdownOpen}
                aria-haspopup="true"
              >
                Info
                <span className={`dropdown-arrow ${infoDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
              <div className={`info-dropdown-menu ${infoDropdownOpen ? 'active' : ''}`}>
                <NavLink 
                  to="/info/rules" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => setInfoDropdownOpen(false)}
                >
                  Rules
                </NavLink>
                <NavLink 
                  to="/info/prizepool" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => setInfoDropdownOpen(false)}
                >
                  Prize Pool
                </NavLink>
                <NavLink 
                  to="/info/format" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => setInfoDropdownOpen(false)}
                >
                  Format
                </NavLink>
                <NavLink 
                  to="/info/schedule" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => setInfoDropdownOpen(false)}
                >
                  Schedule
                </NavLink>
                <NavLink 
                  to="/info/faq" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => setInfoDropdownOpen(false)}
                >
                  FAQ
                </NavLink>
                <NavLink 
                  to="/about" 
                  className={({ isActive }) => isActive ? 'active' : ''}
                  onClick={() => setInfoDropdownOpen(false)}
                >
                  About Us
                </NavLink>
              </div>
            </li>
            {/* Show Admin link for admin users */}
            {isAdmin && (
              <li>
                <NavLink
                  to="/admin/dashboard"
                  className={({ isActive }) => isActive ? 'active' : ''}
                >
                  Admin Panel
                </NavLink>
              </li>
            )}
          </ul>
        </nav>

        {/* Desktop Search Bar */}
        <div className="search-container">
          <SearchBar />
        </div>

        {/* Desktop Login */}
        <div className="login-container">
          {user ? (
            <div className="profile-menu" ref={dropdownRef}>
              <button 
                className={`profile-button ${dropdownOpen ? 'active' : ''}`} 
                onClick={toggleDropdown}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                {/* <img src={getProfileImage()} alt="Profile" className="profile-pic" /> */}
                <span>{user.playerName}</span>
              </button>
              <div className={`profile-dropdown ${dropdownOpen ? 'active' : ''}`}>
                <Link 
                  to={`/profile/${user.steamID}`} 
                  className="dropdown-item"
                  onClick={() => setDropdownOpen(false)}
                >
                  My Profile
                </Link>
                {!isAdmin && (
                  <Link 
                    to="/admin/login" 
                    className="dropdown-item admin-link"
                    onClick={handleAdminLogin}
                  >
                    Admin Login
                  </Link>
                )}
                <button className="dropdown-item logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <>
              <button className="login-button" onClick={handleSteamLogin}>
                Login with Steam
              </button>
              {/* Admin link removed */}
            </>
          )}
        </div>

      </div>
    </header>
  );
};

export default Header;